import { WPAjax } from '../wp-ajax';
import BillTrackerList from './list';
import BillTrackerTable from './table';

/* eslint-disable camelcase */

export type BillData = {
	id: string;
	primary_external_id: string;
	locality_code: string;
	title: string;
	status: keyof typeof STATUS_MAP;
	type: string;
	action_date: string;
	source: string;
	keywords: string[];
	category_group: 'pro' | 'anti';
	state: string;
	statusCategory: string;
	categories: Array<string>;
	is_current_law: boolean;
};

export interface BillDataWithElements extends BillData {
	elements: {
		listItem: HTMLElement;
		row: HTMLTableRowElement;
	};
}

export interface BillCategory {
	hide_in_filters?: boolean;
	title?: string;
	labels?: number[];
	issues?: number;
}

export interface BillsResponseData {
	categoryGroups: { [groupName: string]: { [key: string]: BillCategory } };
	bills: Array<BillDataWithElements>;
}

export interface HighchartsStateTopologyProperties {
	'hc-a2': string; // "MA"
	'hc-key': string; // ""us-ma""
	name: string; // "Massachusetts"
}

export interface HighchartsTopologyData {
	objects: {
		default: {
			geometries: Array<{
				properties: HighchartsStateTopologyProperties;
			}>;
		};
	};
}

export interface StateWithBills extends HighchartsStateTopologyProperties {
	bills: Array<BillData>;
	totalActiveBills: number;
}

export type ColumnOrder = 'ascending' | 'descending';

export interface SortParam {
	column: keyof BillData;
	order: ColumnOrder;
}

export type TableHeadings = keyof typeof TABLE_HEADINGS;

const US_TOPOLOGY_SOURCE =
	'//code.highcharts.com/mapdata/countries/us/us-all.topo.json';

const STATUS_MAP = {
	INTRODUCED: `Introduced`,
	PASSED_FIRST_CHAMBER: `Passed First Chamber`,
	PASSED_SECOND_CHAMBER: `Passed Second Chamber`,
	ENACTED: `Enacted`,
	FAILED: `Failed`,
	VETOED: `Vetoed`,
	FAILED_SINE_DIE: `Failed`,
};

const promisesStore = {
	usTopologyData: <Promise<HighchartsTopologyData> | null>null,
	billsData: <Promise<BillsResponseData> | null>null,
};

export const TABLE_HEADINGS: { [key in keyof Partial<BillData>]: string } = {
	title: 'Bill Name',
	state: 'State',
	primary_external_id: 'Bill No.',
	action_date: 'Action Date',
	statusCategory: 'Status',
};

export const COLUMN_SORT_PRIORITY: TableHeadings[] = ['state', 'action_date'];

export const DEFAULT_SORT_PARAMS: SortParam[] = [
	{ column: 'state', order: 'ascending' },
];

export const usTopologyData = () => {
	if (!(promisesStore.usTopologyData instanceof Promise)) {
		promisesStore.usTopologyData = new Promise(async (resolve) => {
			try {
				const topologyData: HighchartsTopologyData = await fetch(
					US_TOPOLOGY_SOURCE
				).then((res) => res.json());
				resolve(topologyData);
			} catch (err) {
				throw err;
			}
		});
	}

	return promisesStore.usTopologyData;
};

async function billTrackerRequest() {
	let triesLeft = 3;

	// A server error might be caused by the request crashing within php curl
	// we can retry this request till we get a response
	while (triesLeft--) {
		const response = await WPAjax<BillsResponseData>({
			action: 'bill_tracker',
		});

		return response;
	}

	return Promise.reject();
}

export function billsData() {
	if (!(promisesStore.billsData instanceof Promise)) {
		promisesStore.billsData = new Promise(async (resolve) => {
			try {
				const [{ data }, statesProperties] = await Promise.all([
					await billTrackerRequest(),
					getStatesProperties(),
				]);

				const stateCodeToName = statesProperties.reduce(
					(
						codeToName,
						stateProps: HighchartsStateTopologyProperties
					) => {
						codeToName[stateProps['hc-key']] = stateProps;
						return codeToName;
					},
					{} as {
						[key: string]: HighchartsStateTopologyProperties;
					}
				);

				const sanitizedData: BillsResponseData = {
					categoryGroups: data.category_groups,
					bills: data.bills.map((bill: BillDataWithElements) => {
						bill.title = bill.title.replace(/\\/g, '');
						bill.statusCategory = getStatusCategory(bill.status);
						bill.state =
							stateCodeToName[
								bill.locality_code.toLocaleLowerCase()
							]?.name || '';

						// Build elements
						bill.elements = {
							row: BillTrackerTable.generateBillRowElement(bill),
							listItem:
								BillTrackerList.generateBillListItemElement(
									bill
								),
						};

						return bill;
					}),
				};

				resolve(sanitizedData);
			} catch (err) {
				throw err;
			}
		});
	}

	return promisesStore.billsData;
}

/**
 * Gets all states topology properties
 */
export const getStatesProperties = async () => {
	// Extract data
	const usTopology = await usTopologyData();

	return usTopology.objects.default.geometries.map(
		({ properties }) => properties
	);
};

/**
 * Gets a single state properties.
 *
 * @param stateCode e.g., NY
 * @param property
 */
export const getSingleStateProperty = async (
	stateCode: string,
	property: keyof HighchartsStateTopologyProperties | 'all' = 'all'
): Promise<HighchartsStateTopologyProperties | string | null> => {
	// Extract data
	const usTopology = await usTopologyData();

	const matchedState = usTopology.objects.default.geometries.find(
		({ properties }) => properties['hc-a2'] === stateCode
	);

	if (matchedState) {
		return property === 'all'
			? matchedState.properties
			: matchedState.properties[property];
	}

	return null;
};

/**
 * Gets a single state topology data.
 *
 * @param stateCode - state code.  e.g., "NY"
 */
export const getStateTopology = async (stateCode: string = '') => {
	// Ensure uppercase code
	const localityCode = stateCode.toUpperCase();

	// Extract data
	const statesProperties = await getStatesProperties();

	return statesProperties.find(
		(properties) => properties['hc-a2'] === localityCode
	);
};

/**
 * Gets state data:
 * - topology
 * - bills
 *
 * @param stateCode - hyphenated locality code.  e.g., US-NY
 */
export const getStateData = async (stateCode: string = '') => {
	// Ensure uppercase code
	const localityCode = stateCode.toUpperCase();

	// Extract data
	const [topology, bills] = await Promise.all([
		usTopologyData(),
		getStateBills(localityCode),
	]);

	return {
		topology,
		bills,
	};
};

/**
 * Gets
 */
export const getStatesWithBills = async () => {
	const [states, bills] = await Promise.all([
		getStatesProperties(),
		billsData(),
	]);

	const statesWithBills: StateWithBills[] = states.map((state) => {
		const stateBills = bills.bills.filter(
			(bill) => state['hc-key'].toUpperCase() === bill.locality_code
		);

		const totalActiveBills = stateBills.filter(({ status }) =>
			/INTRODUCED|PASSED_FIRST_CHAMBER|PASSED_SECOND_CHAMBER|ENACTED/.test(
				status
			)
		).length;

		return {
			...state,
			totalActiveBills,
			bills: stateBills,
		};
	});

	return statesWithBills;
};

/**
 * Gets state bills
 *
 * @param stateCode - hyphenated locality code.  e.g., US-NY
 */
export const getStateBills = async (stateCode: string = '') => {
	// Ensure uppercase code
	const localityCode = stateCode.toUpperCase();

	// Filter out bills for this given state
	const bills = (await billsData()).bills.filter(
		(bill) => bill.locality_code === localityCode
	);

	return bills;
};

export const getStatusCategory = (billStatus: keyof typeof STATUS_MAP) =>
	STATUS_MAP[billStatus] || '';

export const sortBills = (
	bills: BillData[] = [],
	sortParams: SortParam[] = []
) => {
	// Ensure to sort by proper column order
	const sortColumns = sortParams.map(({ column }) => column);
	const sortParamsPrioritized = COLUMN_SORT_PRIORITY.filter((column) =>
		sortColumns.includes(column)
	).map(
		(column) =>
			sortParams.find(
				({ column: _column }) => _column === column
			) as SortParam
	);

	// Duplicate bills list
	const sortedBills = bills.slice();

	sortParamsPrioritized.forEach(({ column, order }, index) => {
		const sortMultiplier = order === 'ascending' ? 1 : -1;

		sortedBills.sort((billA, billB) => {
			// For non-first sortParam, comparison will only be relevant
			// for items with the same previous column
			if (index) {
				const previousColumn = sortParams[index - 1].column;
				if (billA[previousColumn] !== billB[previousColumn]) {
					return 0;
				}
			}

			const comparison =
				(billA[column] > billB[column]
					? 1
					: billA[column] < billB[column]
					? -1
					: 0) * sortMultiplier;

			return comparison;
		});
	});

	return sortedBills;
};
